import { Injectable } from "@angular/core";
import { filter, first, map, switchMap } from "rxjs/operators";
import { BehaviorSubject, firstValueFrom, from, Observable, of, throwError } from "rxjs";
import { Strings } from "../classes/messages";
import { LoadingController } from "@ionic/angular";
import { StorageService } from "./storage.service";
import { ToolsService } from "./tools.service";
import { NotificationService } from "./notification.service";
import { Session } from "../models/session/session.model";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import * as moment from "moment";
import { Config } from "../config";
import { BuilderPatternService } from "./builder-pattern.service";
import { StepperService } from "./stepper.service";
import { SessionService } from "./session.service";
import { LoggingService } from "./logging.service";
import { LicenseSession } from "../models/license/license-session.model";
import { TraceIdService } from "./tools/trace-id.service";
import { LicenseValidationService } from "./license-validation.service";

@Injectable({
    providedIn: "root",
})
export class AuthenticationService {
    get api(): string {
        return Config.environment.apiDynamic
            ? `${window.location.protocol}//${window.location.host}`
            : Config.environment.api;
    }

    private session$: BehaviorSubject<Session> = new BehaviorSubject<Session>(
        null,
    );

    get session(): Observable<Session> {
        return this.session$.asObservable();
    }

    constructor(
        private loadingCtrl: LoadingController,
        private storageSrv: StorageService,
        private toolsSrv: ToolsService,
        private notifySrv: NotificationService,
        private http: HttpClient,
        private builderPatternSrv: BuilderPatternService,
        private stepperSrv: StepperService,
        private sessionSrv: SessionService,
        private loggingService: LoggingService,
        private traceIdService: TraceIdService,
        private licenseValidationService: LicenseValidationService,
    ) {
        this.listenUserSession();
    }

    public async factoryLoadUserSession(): Promise<void> {
        const loadingRef = await this.loadingCtrl.create({
            cssClass: "app-loading",
            message: Strings.pleaseAwaitMessage,
            showBackdrop: true,
            spinner: "crescent",
            duration: 0,
        });
        try {
            await loadingRef.present();
            const userSession = await this.getUserSession()
                .pipe(first())
                .toPromise();
            await this.setUserSession(userSession);
            const step = await this.storageSrv.getStep();
            this.updateStepPosition(step);
            await loadingRef.dismiss();
        } catch (error) {
            this.logout();
            await this.notifySrv.showToastDanger(
                error.error ? error.error.message : "Ha ocurrido un error",
            );
            setTimeout(() => {
                this.toolsSrv.goRouteLoginPage();
            }, 100);
            this.loggingService.error("Authentication service error:", false, error);
            await loadingRef.dismiss();
            throw new Error(error);
        }
    }

    public login(email: string, password: string): Observable<Session> {
        const data = { email, password };
        return this.http.post(`${this.api}/api/v1/sign-in`, data).pipe(
            map((response: any) => {
                response.expirationDate = moment()
                    .add(1, "hours")
                    .format("YYYY-MM-DD HH:mm:ss");
                const session =
                    this.builderPatternSrv.buildSessionClass(response);
                this.updateSessionSubject(session);
                this.storageSrv.setSession(session);
                return session;
            }),
        );
    }

    public companyMe(): Observable<any> {
        return of(this.storageSrv.getLocalStorageDecrypted("_cap_session")?.accessToken)
            .pipe(
                map(accessToken => {
                    if (accessToken) {
                        return accessToken;
                    }
                    throw new Error("accessToken not found");
                }),
                switchMap((accessToken) =>
                    this.http.get(`${this.api}/api/v1/company/me`, {
                        headers: {
                            authorization: `Bearer ${accessToken}`,
                        },
                    }),
                ),
            );
    }

    public signInByCode(code: string, device: string): Observable<Session> {
        const headers = new HttpHeaders({
            "Short-Key": code,
            "Origin-Device": device,
        });

        return this.http
            .post(`${this.api}/api/v1/kyc/session`, {}, { headers, observe: 'response' })
            .pipe(
                map((httpResponse: HttpResponse<any>) => {
                    // Capture X-Trace-ID header from response
                    const traceId = httpResponse.headers.get('X-Trace-ID');
                    if (traceId) {
                        this.traceIdService.setSessionTraceId(traceId);
                        this.loggingService.log("🔍 [signInByCode] X-Trace-ID captured from /kyc/session:", false, { traceId });

                        // Activate license validation when X-Trace-ID is received
                        const licenseConfig = Config.environment.license;
                        if (licenseConfig?.enabled) {
                            this.loggingService.log("🔑 [signInByCode] X-Trace-ID received, activating license validation");
                            // Trigger license validation asynchronously (don't block the current flow)
                            this.validateLicenseWithTraceId(traceId).catch(error => {
                                this.loggingService.warn("⚠️ [signInByCode] License validation failed:", false, error);
                            });
                        }
                    } else {
                        this.loggingService.warn("⚠️ [signInByCode] X-Trace-ID header not found in /kyc/session response");
                    }

                    const response = httpResponse.body;
                    response.document = { country: response.document };
                    response.expirationDate = moment()
                        .add(14, "minutes")
                        .format();


                    const session = Session.from(response);

                    this.storageSrv.setSession(session);
                    this.storageSrv.setEventId(response.sessionId);
                    this.updateStepPosition(response.step);
                    this.updateSessionSubject(session);
                    this.sessionSrv.setSession(session);

                    return session;
                }),
            );
    }

    public updateSessionSubject(session: Session | null): void {
        this.session$.next(session ?? null);
    }

    private listenUserSession(): void {
        this.getUserSession().subscribe(this.setUserSession);
    }

    private getUserSession(): Observable<Session> {
        return from(this.storageSrv.getSession());
    }

    private setUserSession = async (session: Session) => {
        if (session && !session.hasExpired()) {
            try {
                this.session$.next(this.builderPatternSrv.buildSessionClass(session));
                this.sessionSrv.setSession(session);
            } catch (error) {
                this.loggingService.error("Authentication service error:", false, error);
                throw new Error(error);
            }
        } else {
            this.session$.next(null);
        }
    };

    public async logout(): Promise<void> {
        await this.storageSrv.removeStorage();
        await this.removeUserSession();
        // Clear session trace ID on logout
        this.traceIdService.clearSessionTraceId();
        this.loggingService.log("🔍 [logout] Session trace ID cleared");
    }

    public async removeUserSession(): Promise<void> {
        await this.storageSrv.removeSession();
        this.updateSessionSubject(null);
    }

    private updateStepPosition(step: string): void {
        if (step) {
            const formattedStep = this.parseStepFormat(step);
            this.stepperSrv.updateStep(formattedStep);
            return;
        }
        this.stepperSrv.updateStep("step1");
    }

    private parseStepFormat(step: string): string {

        const format = {
            "0": "step1",
            "1": "step2",
            "2": "step3",
            "3": "step4",
            "4": "step5",
            "5": "step6",
            "6": "stepList",
            "7": "stepResults",
        };
        const formattedStep = format[step];

        return formattedStep ?? step;
    }

    /**
     * Create a session from a license validation response
     * @param licenseSession - LicenseSession obtained from license validation
     */
    public createSessionFromLicense(licenseSession: LicenseSession): void {
        // Create a minimal session object with the license token
        const sessionData = {
            accessToken: licenseSession.accessToken,
            expirationDate: licenseSession.expiresAt.format(),
            user: null,
            company: null,
            document: null,
        };

        const session = Session.from(sessionData);

        // Store and update session
        this.storageSrv.setSession(session);
        this.updateSessionSubject(session);
        this.sessionSrv.setSession(session);

        // Update step position from license response
        if (licenseSession.step !== undefined) {
            this.updateStepPosition(licenseSession.step.toString());
        }

        this.loggingService.log("Session created from license", false, {
            session_id: licenseSession.sessionId,
            jti: licenseSession.jti,
            step: licenseSession.step,
        });
    }

    /**
     * Validate license using the trace_id from /kyc/session
     * This is triggered when X-Trace-ID header is received
     */
    public async validateLicenseWithTraceId(traceId: string): Promise<boolean> {
        try {
            this.loggingService.log(
                "🔑 [ValidateLicenseWithTraceId] Starting license validation with trace_id",
                false,
                { traceId }
            );

            // Validate license with trace_id (the license field will contain the trace_id)
            const licenseSession = await firstValueFrom(
                this.licenseValidationService.validateLicense(
                    traceId, // This will be sent as the 'license' field (which contains trace_id)
                    window.location.origin
                )
            );

            // Create authentication session from license
            this.createSessionFromLicense(licenseSession);

            this.loggingService.log(
                "✅ [ValidateLicenseWithTraceId] License validated successfully with trace_id",
                false,
                {
                    session_id: licenseSession.sessionId,
                    jti: licenseSession.jti,
                    traceId: traceId,
                }
            );

            return true;
        } catch (error) {
            this.loggingService.error(
                "❌ [ValidateLicenseWithTraceId] License validation with trace_id failed",
                false,
                error
            );
            // Don't block app flow if license validation fails
            return false;
        }
    }
}
